// src/contexts/AuthProvider.tsx

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, onIdTokenChanged } from 'firebase/auth';
import { auth, db } from '@/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { trackGA4 } from '@/utils/analytics';
import { recordFunnel, recordFunnelOncePerSession, flushPendingAnswers } from '@/utils/funnel';
import { defaultHiddenSubjects } from '@/components/SettingsPage'; // 初期の非表示科目設定
import { getUserDoc } from '@/services/firestoreService';

interface AuthContextType {
  user: User | null;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType>({ user: null, loading: true });

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    // ネイティブ / 認証初期化失敗などで永久に読み込み中にならないよう、3秒でフォールバック
    const fallbackTimer = setTimeout(() => {
      setLoading(false);
    }, 3000);

    let unsubscribe: (() => void) | null = null;
    try {
      // Firebase 認証状態の監視
      unsubscribe = onIdTokenChanged(auth, async (currentUser) => {
        try {
          if (currentUser) {
            const refreshedUser = currentUser;
            setUser(refreshedUser);

            const userRef = getUserDoc(currentUser.uid);
            const userSnap = await getDoc(userRef);

            if (!userSnap.exists()) {
              await setDoc(userRef, {
                displayName: refreshedUser?.displayName || '匿名ユーザー',
                email: refreshedUser?.email || '',
                photoURL: refreshedUser?.photoURL || '',
                solvedCount: 0,
                answeredQuestions: [],
                hidden_subjects: defaultHiddenSubjects,
                onboardingCompleted: false,
              });

              // 新規登録の計測（ファネル: sign_up → first_question_answered → session_resumed → paywall_view → checkout_start → purchase）
              // GA4 と Firestore users/{uid}/funnel の両方に残す
              // 静的ミニ模試で解いた分があれば回収する（登録前に解いていた場合）
              void flushPendingAnswers(currentUser.uid);

              void recordFunnel('sign_up', currentUser.uid, {
                method: refreshedUser?.providerData?.[0]?.providerId || 'unknown',
              });

              try {
                localStorage.setItem('needsTutorial', '1');
              } catch (_) { /* noop */ }
            } else {
              // 継続の計測: 既存ユーザーがセッションを開始した（1ブラウザセッションにつき1回）
              void flushPendingAnswers(currentUser.uid);
              void recordFunnelOncePerSession('session_resumed', currentUser.uid, {
                method: refreshedUser?.providerData?.[0]?.providerId || 'unknown',
              });

              const userData = userSnap.data();
              const needsUpdate =
                userData.displayName !== (refreshedUser?.displayName || '匿名ユーザー') ||
                userData.email !== (refreshedUser?.email || '') ||
                userData.photoURL !== (refreshedUser?.photoURL || '');

              if (needsUpdate) {
                await setDoc(userRef, {
                  displayName: refreshedUser?.displayName || '匿名ユーザー',
                  email: refreshedUser?.email || '',
                  photoURL: refreshedUser?.photoURL || '',
                }, { merge: true });
              }
            }
          } else {
            setUser(null);
          }
        } catch (err) {
          // 【2026-10-12 修正】ここで setUser(null) していたため、
          // **Firestore の読み取りが1本転んだだけで画面がログアウト扱いになっていた**。
          // この callback は setUser(currentUser) の**後**に users/{uid} を読むので、
          // 回線が詰まった回は getDoc が「client is offline」で落ち、
          // ログイン済みの利用者に「ログインしてデータを取得してください」が出る。
          // 実測（10/12・本番の新しい版）: Firestore 行きを握って返さないと 14 秒で
          // ログイン促しの画面になった。導線ガードの「学習管理に解答が載る」が
          // 09-27 と 10-12 にこの形で赤になっている。
          // **誰がログインしているかを決めるのは Auth で、Firestore の都合ではない。**
          // 利用者情報の読み書きが失敗しても、ログイン状態はそのまま保つ。
          console.warn('[AuthProvider] 利用者情報の読み書きに失敗（ログイン状態は保つ）:', err);
        } finally {
          setLoading(false);
        }
      }, (err) => {
        // Auth初期化・監視エラー時
        console.warn('[AuthProvider] Auth listener error:', err);
        setUser(null);
        setLoading(false);
      });
    } catch (err) {
      console.warn('[AuthProvider] Failed to initialize auth listener:', err);
      setLoading(false);
    }

    return () => {
      clearTimeout(fallbackTimer);
      if (unsubscribe) unsubscribe();
    };
  }, []);

  return <AuthContext.Provider value={{ user, loading }}>{children}</AuthContext.Provider>;
};

export default AuthProvider;

